/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import "../styles/MatchPage.css";
import MultiStepBar from "../components/MultiStepBar";
import { computeTopSim } from "../api/computeTopSim"; // Import the API function

export default function MatchPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterParty, setFilterParty] = useState("All Parties");
  const [sortBy, setSortBy] = useState("match");
  const [filteredMatches, setFilteredMatches] = useState([]);
  const steps = ["Select Areas", "Swipe Cards", "See Matches"];
  const currentStep = 3; // for example

  const findHighestScore = (matches) => {
    // Check if matches array exists and has elements
    if (!matches || matches.length === 0) {
      return 0; // Return 0 or any default value if matches is empty
    }
    
    // Initialize with the first match's score (or 0 if score is undefined)
    let highestScore = matches[0]?.score || 0;
    let mpName = matches[0]?.name || 'Not found';
    let party = matches[0]?.party|| 'Not found';
    
    // Loop through all matches to find the highest score
    matches.forEach(match => {
      if (match && match.score && match.score > highestScore) {
        highestScore = match.score
        mpName = match.name
        party = match.party
      }
    });
    
    return [highestScore,mpName, party] ;
  };

  useEffect(() => {
    // Check for match results from the navigation state
    const matchResults = location.state?.matchResults;

    if (matchResults) {
      // Use the results from the API
      setMatches(matchResults);
      setFilteredMatches(matchResults);
      setLoading(false);
    } else {
      // Fallback to getting responses and computing matches
      let responses = location.state?.responses;
      if (!responses) {
        const stored = localStorage.getItem("responses");
        if (stored) responses = JSON.parse(stored);
      }

      if (!responses || responses.length === 0) {
        navigate("/policy-swipe/swipe");
        return;
      }

      // Try to compute matches again if needed
      fetchMatches(responses);
    }
  }, [location, navigate]);

  // Apply filters and sorting
  useEffect(() => {
    let filtered = [...matches];

    // Apply party filter
    if (filterParty !== "All Parties") {
      filtered = filtered.filter((mp) => mp.party === filterParty);
    }

    // Apply sorting
    if (sortBy === "match") {
      filtered.sort((a, b) => b.score - a.score); // Highest match first
    } else if (sortBy === "name") {
      filtered.sort((a, b) => a.name.localeCompare(b.name)); // Alphabetical
    } else if (sortBy === "party") {
      filtered.sort((a, b) => a.party.localeCompare(b.party)); // By party
    }

    setFilteredMatches(filtered);
  }, [filterParty, sortBy, matches]);

  const handleFilterChange = (e) => {
    setFilterParty(e.target.value);
  };

  const handleSortChange = (e) => {
    setSortBy(e.target.value);
  };

  const restartMatching = () => {
    navigate("/policy-areas");
  };

  const viewProfile = (mpId) => {
    // navigate(`/mp/${mpId}`);
  };

  // Mock data generation (in a real app, this would come from your backend)
  const generateMockMatches = () => {
    const parties = ["Labor", "Liberal", "National", "Greens", "Independent"];
    const electorates = [
      "Sydney",
      "Melbourne",
      "Brisbane",
      "Perth",
      "Adelaide",
      "Canberra",
      "Darwin",
      "Hobart",
      "Newcastle",
      "Gold Coast",
    ];
    const topIssuesList = [
      "Climate Action",
      "Economic Reform",
      "Healthcare Funding",
      "Education Policy",
      "Tax Reform",
      "Immigration",
      "Social Services",
      "Defense Spending",
      "Housing Affordability",
      "Infrastructure",
      "Renewable Energy",
      "Aged Care",
    ];

    return Array.from({ length: 15 }, (_, i) => {
      // Generate a decreasing match score (95% to 65%)
      const score = Math.max(65, Math.floor(95 - i * 2.5));

      // Randomly select 2-3 top issues
      const issueCount = Math.floor(Math.random() * 2) + 2; // 2 or 3 issues
      const topIssues = [];

      while (topIssues.length < issueCount) {
        const issue =
          topIssuesList[Math.floor(Math.random() * topIssuesList.length)];
        if (!topIssues.includes(issue)) {
          topIssues.push(issue);
        }
      }

      return {
        id: i + 1,
        name: `MP ${i + 1}`,
        party: parties[Math.floor(Math.random() * parties.length)],
        electorate: electorates[Math.floor(Math.random() * electorates.length)],
        photoUrl: `/api/placeholder/150/150`,
        score: score,
        topIssues: topIssues,
      };
    });
  };

  const fetchMatches = async (responses) => {
    try {
      // Try to use the API function
      const matchResults = await computeTopSim(responses);

      if (matchResults) {
        setMatches(matchResults);
        setFilteredMatches(matchResults);
      } else {
        // Fall back to mock data if API fails
        const mockMatches = generateMockMatches();
        setMatches(mockMatches);
        setFilteredMatches(mockMatches);
      }
    } catch (error) {
      console.error("Error fetching matches:", error);
      // Use mock data as fallback
      const mockMatches = generateMockMatches();
      setMatches(mockMatches);
      setFilteredMatches(mockMatches);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="match-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Calculating your political matches...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="match-page">
      <MultiStepBar steps={steps} currentStep={currentStep} />
      <div className="match-container">
        <div className="match-header">
          <h1>Your Political Matches</h1>
          <p>
            Based on your policy preferences, here are the MPs who best align
            with your views.
          </p>
        </div>

        <div className="match-summary">
          <div className="summary-card">
            <div className="summary-icon">
              <span>🎯</span>
            </div>
            <div className="summary-content">
              <h3>{matches.length} Matches Found</h3>
              <p>MPs ranked by how closely they align with your views</p>
            </div>
          </div>

          <div className="summary-card">
            <div className="summary-icon">
              <span>📊</span>
            </div>
            <div className="summary-content">
              <h3>Top Match: {findHighestScore(matches)[0]}%</h3>
              <p>
                with {findHighestScore(matches)[1]} ({findHighestScore(matches)[2]})
              </p>
            </div>
          </div>

          <div className="summary-card">
            <div className="summary-icon">
              <span>🗳️</span>
            </div>
            <div className="summary-content">
              <h3>Parties Represented</h3>
              <p>
                {Array.from(new Set(matches.map((m) => m.party))).length}{" "}
                different parties
              </p>
            </div>
          </div>
        </div>

        <div className="filter-controls">
          <div className="filter-group">
            <label>Filter by Party:</label>
            <select value={filterParty} onChange={handleFilterChange}>
              <option>All Parties</option>
              {Array.from(new Set(matches.map((m) => m.party))).map((party) => (
                <option key={party}>{party}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Sort by:</label>
            <select value={sortBy} onChange={handleSortChange}>
              <option value="match">Match %</option>
              <option value="name">Name</option>
              <option value="party">Party</option>
            </select>
          </div>
        </div>

        <div className="mp-profile-grid">
          {filteredMatches.length > 0 ? (
            filteredMatches.map((mp) => (
              <div key={mp.id} className="mp-card">
                <div className="mp-header">
                  <div className="match-percent-badge">{mp.score}%</div>
                  {/* <img src={mp.photoUrl} alt={mp.name} className="mp-avatar" /> */}
                </div>
                <div className="mp-content">
                  <h3 className="mp-name">{mp.name}</h3>
                  <p className="mp-party">
                    {mp.party} for {mp.electorate}
                  </p>
                  <div className="match-details">
                    <h4>Top Matching Issues:</h4>
                    <ul className="matching-issues">
                      {mp.topIssues.map((issue, index) => (
                        <li key={index}>{issue}</li>
                      ))}
                    </ul>
                  </div>
                  {/* <button
                    className="btn btn-primary view-profile-btn"
                    onClick={() => viewProfile(mp.id)}
                  >
                    View Full Profile
                  </button> */}
                </div>
              </div>
            ))
          ) : (
            <div className="no-matches">
              <h3>No MPs match your filter criteria</h3>
              <p>Try adjusting your filters to see more results.</p>
            </div>
          )}
        </div>

        <div className="match-actions">
          <button
            className="btn btn-secondary restart-btn"
            onClick={restartMatching}
          >
            Try Different Policy Areas
          </button>
        </div>

        <div className="match-explanation">
          <h3>How We Calculate Matches</h3>
          <p>
            Your match percentage is based on how often an MP has voted in
            alignment with your policy preferences. We analyze parliamentary
            voting records and speeches to determine each MP's stance on the
            issues you swiped on.
          </p>
          <p>
            Priority issues you marked with a star are weighted more heavily in
            our calculations.
          </p>
        </div>
      </div>
    </div>
  );
}
